import { useCallback, useEffect, useMemo, useRef, useState, type ClipboardEvent } from 'react'
import { Link } from 'react-router-dom'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import {
  AlertTriangle,
  ArrowRight,
  Bot,
  Brain,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  Copy,
  FolderPlus,
  Info,
  Star,
  StarOff,
  Trash2,
  User,
} from 'lucide-react'
import type {
  Source,
  TurnStats,
  VerificationReport,
} from '../lib/types'

// ─── Follow-up question parser ────────────────────────────────────────────────
const FOLLOW_UP_HEADING_RE = /(?:#{1,4}\s+)?(?:\*{1,2})?(?:延伸研究问题[：:]|Further Research Questions[：:]?)(?:\*{1,2})?[：:]?/

function parseFollowUpSection(content: string): {
  mainContent: string
  heading: string
  questions: string[]
} | null {
  const match = content.match(FOLLOW_UP_HEADING_RE)
  if (!match || match.index === undefined) return null

  const mainContent = content.slice(0, match.index).trimEnd()
  const afterHeading = content.slice(match.index + match[0].length)

  const questions: string[] = []
  for (const line of afterHeading.split('\n')) {
    const m = line.match(/^\s*\d+[.)\s]\s*(.+)$/)
    if (m) questions.push(m[1].trim())
  }

  if (questions.length === 0) return null
  return { mainContent, heading: match[0].replace(/^#{1,4}\s+/, '').replace(/\*{1,2}/g, '').replace(/[：:]+$/, '').trim(), questions }
}

function stripCitations(text: string): string {
  return text
    .replace(/\s*\[[\d,\s]+\]/g, '')               // [1], [12], [1, 2]
    .replace(/\s+\d+(?:,\s*\d+)+(?=[?\s]*$)/, '') // bare "12, 15" near end
    .trim()
}
import { useI18n } from '../lib/i18n'
import { useSnippetContext, DEFAULT_FAVORITES_NAME, DEFAULT_MAIN_FOLDER_NAME } from '../contexts/SnippetContext'
import { OperationSteps } from './OperationSteps'
import { ChatDisclaimer } from './AIDisclaimer'

interface BubbleProps {
  role: 'user' | 'assistant'
  content: string
  sources?: Source[]
  stats?: TurnStats
  /** Grounding-check result for this turn.  When `unverified_count > 0`
   *  the bubble shows a warning panel listing the suspicious refs. */
  verification?: VerificationReport
  /** The model's chain-of-thought / analysis for this turn.  When
   *  present (assistant only), a collapsible "thinking" panel is shown
   *  above the answer. */
  reasoning?: string
  /** Structured operation steps showing AI's workflow */
  steps?: Array<{
    action: string
    status: 'start' | 'done'
    details: Record<string, any>
  }>
  isStreaming?: boolean
  onDelete?: () => void
  onFillInput?: (text: string) => void
}

export function MessageBubble({
  role,
  content,
  sources,
  stats,
  verification: _verification,
  reasoning,
  steps,
  isStreaming,
  onDelete,
  onFillInput,
}: BubbleProps) {
  const { s, lang } = useI18n()
  const isUser = role === 'user'
  const [sourcesOpen, setSourcesOpen] = useState(false)
  const [reasoningOpen, setReasoningOpen] = useState(false)
  const hasReasoning = !isUser && !!reasoning && reasoning.trim().length > 0
  const hasSteps = !isUser && !!steps && steps.length > 0

  // 自动展开/关闭思考过程：
  // - 流式输出中且尚无答案内容时自动展开（展示检索步骤）
  // - 答案内容开始输出后自动折叠
  useEffect(() => {
    if (isStreaming) {
      if (!content || content.trim().length === 0) {
        setReasoningOpen(true)
      } else {
        setReasoningOpen(false)
      }
    }
  }, [isStreaming, content])
  const {
    folders = [],
    refreshFolders,
    saveSnippet,
    createFolder,
    toggleFavorite,
  } = useSnippetContext()
  const [showSave, setShowSave] = useState(false)
  const [selectedFolder, setSelectedFolder] = useState<string | null>(null)
  const [newFolderName, setNewFolderName] = useState('')
  const [creatingFolder, setCreatingFolder] = useState(false)
  const [saving, setSaving] = useState(false)
  const [favoriting, setFavoriting] = useState(false)
  const [favoriteStatus, setFavoriteStatus] = useState(false)
  const [favoriteSnippetId, setFavoriteSnippetId] = useState<string | null>(null)
  const [statusMessage, setStatusMessage] = useState('')
  const [statusLevel, setStatusLevel] = useState<'info' | 'error'>('info')
  const statusTimer = useRef<number | null>(null)
  const favoritesFolder = useMemo(
    () => folders.find((folder) => folder.name === DEFAULT_FAVORITES_NAME),
    [folders],
  )
  const mainFolder = useMemo(
    () => folders.find((folder) => folder.name === DEFAULT_MAIN_FOLDER_NAME || folder.is_main),
    [folders],
  )
  const sanitizedContent = useMemo(() => {
    if (!content) return content
    // Remove standalone thematic-break lines (--- / ___ / ***) anywhere in the
    // content so they don't render as full-width <hr> elements.
    return content.replace(/^[ \t]*[-*_]{3,}[ \t]*$/gm, '')
  }, [content])

  const followUpParsed = useMemo(() => {
    if (isUser || isStreaming || !sanitizedContent) return null
    return parseFollowUpSection(sanitizedContent)
  }, [isUser, isStreaming, sanitizedContent])
  const copyMirrorRef = useRef<HTMLDivElement>(null)
  const markdownComponents = useMemo(
    () => ({
      hr: () => (
        <hr className="my-2 border-t border-brand-200" />
      ),
      table: (props: React.ComponentProps<'table'>) => (
        <table {...props} className="w-full border-collapse border-none" />
      ),
      th: (props: React.ComponentProps<'th'>) => (
        <th {...props} className="border border-transparent text-left font-semibold" />
      ),
      td: (props: React.ComponentProps<'td'>) => (
        <td {...props} className="border border-transparent" />
      ),
    }),
    [],
  )

  useEffect(() => {
    if (!statusMessage) return
    if (statusTimer.current) {
      window.clearTimeout(statusTimer.current)
    }
    statusTimer.current = window.setTimeout(() => {
      setStatusMessage('')
      statusTimer.current = null
    }, 2500)
    return () => {
      if (statusTimer.current) {
        window.clearTimeout(statusTimer.current)
        statusTimer.current = null
      }
    }
  }, [statusMessage])

  useEffect(() => {
    if (!selectedFolder && mainFolder) {
      setSelectedFolder(mainFolder.folder_id)
    }
  }, [mainFolder, selectedFolder])

  const handleCopy = async () => {
    if (!content) return
    try {
      const mirrorText = copyMirrorRef.current?.innerText?.trim()
      const toCopy = mirrorText && mirrorText.length > 0 ? mirrorText : sanitizedContent
      if (!toCopy) return
      await navigator.clipboard.writeText(toCopy)
      setStatusMessage(s.copySuccess)
      setStatusLevel('info')
    } catch (err) {
      console.error('Copy failed', err)
      setStatusMessage(s.copyFailed)
      setStatusLevel('error')
    }
  }

  const handleSelectionCopy = useCallback(
    (event: ClipboardEvent<HTMLDivElement>) => {
      const mirrorText = copyMirrorRef.current?.innerText?.trim()
      const toCopy = mirrorText && mirrorText.length > 0 ? mirrorText : sanitizedContent
      if (!toCopy) return
      event.preventDefault()
      event.clipboardData?.setData('text/plain', toCopy)
      event.clipboardData?.setData('text/html', toCopy)
      setStatusMessage(s.copySuccess)
      setStatusLevel('info')
    },
    [sanitizedContent, s],
  )

  const handleFavorite = async () => {
    if (!content.trim()) return
    setFavoriting(true)
    try {
      if (favoriteStatus && favoriteSnippetId) {
        await toggleFavorite(favoriteSnippetId, false)
        setFavoriteStatus(false)
        setFavoriteSnippetId(null)
        setStatusMessage(s.unfavorited)
        setStatusLevel('info')
      } else {
        const snippet = await saveSnippet({
          content,
          folder_id: favoritesFolder?.folder_id,
          favorite: true,
        })
        setFavoriteSnippetId(snippet.snippet_id)
        setFavoriteStatus(true)
        setStatusMessage(s.favorited)
        setStatusLevel('info')
      }
    } catch (err) {
      console.error('Favorite failed', err)
      setStatusMessage(favoriteStatus ? s.favoriteRemoveFailed : s.favoriteFailed)
      setStatusLevel('error')
    } finally {
      setFavoriting(false)
    }
  }

  const handleCreateFolder = async () => {
    const title = newFolderName.trim()
    if (!title) return
    setCreatingFolder(true)
    try {
      const folder = await createFolder(title)
      await refreshFolders()
      setSelectedFolder(folder.folder_id)
      setNewFolderName('')
      setStatusMessage(s.folderCreated)
      setStatusLevel('info')
    } catch (err) {
      console.error('Create folder failed', err)
      setStatusMessage(s.folderCreateFailed)
      setStatusLevel('error')
    } finally {
      setCreatingFolder(false)
    }
  }

  const handleSaveSnippet = async () => {
    if (!content.trim()) return
    setSaving(true)
    try {
      await saveSnippet({
        content,
        folder_id: selectedFolder ?? mainFolder?.folder_id,
      })
      setStatusMessage(s.saved)
      setStatusLevel('info')
      setShowSave(false)
    } catch (err) {
      console.error('Save snippet failed', err)
      setStatusMessage(s.saveFailed)
      setStatusLevel('error')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className={`flex gap-3 py-4 ${isUser ? 'justify-end' : ''}`}>
      {!isUser && (
        <div className="flex-shrink-0 w-8 h-8 rounded-full bg-brand-700 text-white flex items-center justify-center">
          <Bot className="w-5 h-5" />
        </div>
      )}

      <div className={`max-w-3xl ${isUser ? 'order-1' : ''}`} onCopy={handleSelectionCopy}>
        <div
          className={
            isUser
              ? 'bg-brand-700 text-white rounded-2xl rounded-tr-sm px-4 py-3'
              : 'bg-white border border-brand-200 rounded-2xl rounded-tl-sm px-5 py-4 shadow-sm'
          }
        >
          {isUser ? (
            <p className="whitespace-pre-wrap text-sm leading-relaxed">
              {sanitizedContent}
            </p>
          ) : (
            <div className="prose-brand text-sm">
              {/* 思考过程 + 操作步骤 合并面板 */}
              {(hasReasoning || hasSteps) && (
                <div className="not-prose mb-3 rounded-lg border border-brand-100 bg-brand-50/60">
                  <button
                    type="button"
                    onClick={() => setReasoningOpen((prev) => !prev)}
                    className="flex w-full items-center gap-1.5 px-3 py-2 text-[12px] font-medium text-brand-500 hover:text-brand-700"
                  >
                    <Brain className="h-3.5 w-3.5" />
                    <span>{isStreaming && !content ? s.thinking : s.thoughtProcess}</span>
                    {reasoningOpen ? (
                      <ChevronDown className="ml-auto h-3.5 w-3.5" />
                    ) : (
                      <ChevronRight className="ml-auto h-3.5 w-3.5" />
                    )}
                  </button>
                  {reasoningOpen && (
                    <div className="border-t border-brand-100 px-3 py-2 text-[12px] leading-relaxed text-brand-500 space-y-2">
                      {steps && steps.length > 0 && (
                        <OperationSteps steps={steps} lang={lang} isStreaming={isStreaming} />
                      )}
                      {hasReasoning && (
                        <div className="whitespace-pre-wrap">{reasoning}</div>
                      )}
                    </div>
                  )}
                </div>
              )}
              {sanitizedContent ? (
                <>
                  <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>
                    {followUpParsed ? followUpParsed.mainContent : sanitizedContent}
                  </ReactMarkdown>
                  {followUpParsed && (
                    <div className="mt-3 not-prose">
                      <p className="text-sm font-bold text-brand-800 mb-2">{followUpParsed.heading}</p>
                      <div className="space-y-1">
                        {followUpParsed.questions.map((q, i) => {
                          const cleanQ = stripCitations(q)
                          return (
                            <button
                              key={i}
                              type="button"
                              onClick={() => onFillInput?.(cleanQ)}
                              className={`w-full text-left flex gap-2 items-start text-sm text-blue-700 px-2 py-1.5 rounded-lg transition-colors group ${
                                onFillInput
                                  ? 'hover:bg-blue-50 hover:text-blue-900 cursor-pointer'
                                  : 'cursor-default'
                              }`}
                            >
                              <span className="font-medium flex-shrink-0 text-blue-400 select-none">{i + 1}.</span>
                              <span className="leading-snug">{cleanQ}</span>
                              {onFillInput && (
                                <ArrowRight className="w-3.5 h-3.5 flex-shrink-0 mt-0.5 ml-auto opacity-0 group-hover:opacity-50 transition-opacity" />
                              )}
                            </button>
                          )
                        })}
                      </div>
                    </div>
                  )}
                </>
              ) : (
                <span className="text-brand-400 italic">
                  {isStreaming ? s.generating : s.empty}
                </span>
              )}
              {isStreaming && content && (
                <span className="inline-block w-2 h-4 bg-brand-500 ml-0.5 animate-pulse align-middle" />
              )}
              {!isStreaming && content && (
                <ChatDisclaimer />
              )}
            </div>
          )}
        </div>

        {!isUser && (
          <div className="mt-2 ml-1 flex gap-2 flex-wrap items-center text-[11px]">
            <button
              type="button"
              onClick={handleCopy}
              className="flex items-center gap-1 rounded-full border border-brand-200 px-2 py-1 text-brand-500 hover:border-brand-400 hover:text-brand-700"
            >
              <Copy className="w-3 h-3" />
              {s.copyAll}
            </button>
            <button
              type="button"
              onClick={handleFavorite}
              disabled={favoriting}
              className="flex items-center gap-1 rounded-full border border-brand-200 px-2 py-1 text-brand-500 hover:border-brand-400 hover:text-brand-700 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {favoriteStatus ? (
                <Star className="w-3 h-3 text-yellow-500" />
              ) : (
                <StarOff className="w-3 h-3" />
              )}
              {favoriteStatus ? s.favorited : s.favorite}
            </button>
            {onDelete && (
              <button
                type="button"
                onClick={onDelete}
                className="flex items-center gap-1 rounded-full border border-brand-200 px-2 py-1 text-brand-500 hover:border-brand-400 hover:text-brand-700"
              >
                <Trash2 className="w-3 h-3" />
                {s.deleteTurn}
              </button>
            )}
            <div className="relative">
              <button
                type="button"
                onClick={() => setShowSave((prev) => !prev)}
                className="flex items-center gap-1 rounded-full border border-brand-200 bg-white px-2 py-1 text-brand-500 hover:border-brand-400 hover:text-brand-700"
              >
                <FolderPlus className="w-3 h-3" />
                {s.saveToFolder}
              </button>
              {showSave && (
                <div className="absolute right-0 z-10 mt-2 w-64 rounded-lg border border-brand-200 bg-white p-3 text-[11px] text-brand-600 shadow-lg">
                  <div className="space-y-2">
                    <label className="text-[10px] text-brand-500">{s.folderLabel}</label>
                    <select
                      className="w-full rounded-md border border-brand-200 px-2 py-1 text-sm"
                      value={selectedFolder ?? ''}
                      onChange={(ev) => setSelectedFolder(ev.target.value || null)}
                    >
                      <option value="">{s.chooseFolder}</option>
                      {folders.map((folder) => (
                        <option key={folder.folder_id} value={folder.folder_id}>
                          {folder.name}
                        </option>
                      ))}
                    </select>
                    <div className="flex gap-1">
                      <input
                        className="flex-1 rounded-md border border-brand-200 px-2 py-1 text-sm"
                        placeholder={s.newFolder}
                        value={newFolderName}
                        onChange={(ev) => setNewFolderName(ev.target.value)}
                      />
                      <button
                        type="button"
                        onClick={handleCreateFolder}
                        disabled={creatingFolder}
                        className="rounded-lg bg-brand-500 px-2 py-1 text-white text-xs disabled:opacity-60"
                      >
                        {creatingFolder ? s.creating : s.create}
                      </button>
                    </div>
                    <button
                      type="button"
                      onClick={handleSaveSnippet}
                      disabled={saving}
                      className="w-full rounded-lg bg-brand-700 px-3 py-1 text-white text-xs disabled:opacity-60"
                    >
                      {saving ? s.saving : s.save}
                    </button>
                    {statusMessage && (
                      <p
                        className={`text-[10px] ${
                          statusLevel === 'error' ? 'text-red-600' : 'text-emerald-600'
                        }`}
                      >
                        {statusMessage}
                      </p>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
        {isUser && onDelete && (
          <div className="mt-2 flex justify-end text-[11px]">
            <button
              type="button"
              onClick={onDelete}
              className="flex items-center gap-1 rounded-full border border-brand-200 px-2 py-1 text-brand-500 hover:border-brand-400 hover:text-brand-700"
            >
              <Trash2 className="w-3 h-3" />
              {s.deleteTurn}
            </button>
          </div>
        )}

        {/* hidden mirror used for clipboard. we render sanitized content twice so copy button can grab the cleaned text without disturbing layout */}
        <div
          ref={copyMirrorRef}
          aria-hidden
          className="pointer-events-none absolute inset-0 h-0 w-0 overflow-hidden"
        >
          {sanitizedContent && (
            <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>
              {sanitizedContent}
            </ReactMarkdown>
          )}
        </div>
        {/* Grounding verifier panel hidden — causes confusion for multi-language users */}

        {!isUser && sources && sources.length === 0 && !isStreaming && (
          <div className="mt-2 ml-1 flex items-start gap-2 px-3 py-2 rounded-lg border border-blue-200 bg-blue-50">
            <Info className="w-4 h-4 text-blue-500 flex-shrink-0 mt-0.5" />
            <div className="text-xs text-blue-800">
              <span className="font-semibold">{s.noSourcesWarning}</span>
              <p className="mt-0.5 text-blue-700 leading-snug">{s.noSourcesDesc}</p>
              <Link
                to="/knowledge"
                className="mt-1.5 inline-flex items-center gap-1 text-blue-600 hover:text-blue-800 font-medium"
              >
                {s.noSourcesAction}
                <ArrowRight className="w-3 h-3" />
              </Link>
            </div>
          </div>
        )}

        {!isUser && sources && sources.length > 0 && (
          <div className="mt-2 ml-1">
            <button
              onClick={() => setSourcesOpen(!sourcesOpen)}
              className="text-xs text-brand-500 hover:text-brand-700 flex items-center gap-1 font-medium"
            >
              {sourcesOpen ? (
                <ChevronDown className="w-4 h-4" />
              ) : (
                <ChevronRight className="w-4 h-4" />
              )}
              {s.sourcesN(sources.length)}
            </button>
            {sourcesOpen && (
              <div className="mt-2 space-y-2">
                {sources.map((src) => (
                  <SourceCard key={src.chunk_id} source={src} />
                ))}
              </div>
            )}
          </div>
        )}

        {!isUser && stats && (
          <div className="mt-1 ml-1 text-xs text-brand-400 font-mono flex flex-wrap gap-x-3 gap-y-0.5">
            {stats.retrieval_elapsed_s != null && (
              <span>
                {s.retrieveStat} {stats.retrieval_elapsed_s.toFixed(2)}s
              </span>
            )}
            {stats.generation_elapsed_s != null && (
              <span>
                {s.genStat} {stats.generation_elapsed_s.toFixed(2)}s
              </span>
            )}
            {stats.total_elapsed_s != null && (
              <span>
                {s.totalStat} {stats.total_elapsed_s.toFixed(2)}s
              </span>
            )}
            {stats.prompt_tokens != null && (
              <span>
                {stats.prompt_tokens}p + {stats.completion_tokens ?? '?'}c{' '}
                {s.tokensSuffix}
              </span>
            )}
          </div>
        )}
      </div>

      {isUser && (
        <div className="flex-shrink-0 w-8 h-8 rounded-full bg-brand-400 text-white flex items-center justify-center order-2">
          <User className="w-5 h-5" />
        </div>
      )}
    </div>
  )
}

/** Warning panel that surfaces every Section/case/citation reference the
 *  answer mentions and whether it was found in the retrieved sources.
 *
 *  Two visual states:
 *    - `unverified_count > 0` → amber alert with the suspicious refs
 *      listed by kind.  This is the headline value of the verifier:
 *      lawyers must not act on fabricated Section numbers.
 *    - `unverified_count === 0` → quiet green confirmation chip; lets
 *      the user know the verifier ran and everything checked out.
 */
export function VerificationPanel({ report }: { report: VerificationReport }) {
  const { s } = useI18n()
  const [open, setOpen] = useState(report.unverified_count > 0)

  if (report.unverified_count === 0) {
    // All references verified — minimal confirmation, easy to glance over.
    return (
      <div className="mt-2 ml-1 inline-flex items-center gap-1.5 text-[11px] text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-2 py-0.5">
        <CheckCircle2 className="w-3 h-3" />
        <span>{s.verifierAllGrounded(report.verified_count)}</span>
      </div>
    )
  }

  const unverified = report.references.filter((r) => !r.verified)

  return (
    <div className="mt-2 ml-1 rounded-lg border border-amber-300 bg-amber-50">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="w-full flex items-center gap-2 px-3 py-2 text-xs font-medium text-amber-800 hover:bg-amber-100/60 rounded-lg"
      >
        <AlertTriangle className="w-4 h-4 flex-shrink-0" />
        <span className="flex-1 text-left">
          {s.verifierWarningTitle(
            report.unverified_count,
            report.total,
          )}
        </span>
        {open ? (
          <ChevronDown className="w-4 h-4" />
        ) : (
          <ChevronRight className="w-4 h-4" />
        )}
      </button>
      {open && (
        <div className="px-3 pb-3 pt-1 text-xs text-amber-900">
          <p className="mb-2 leading-snug">{s.verifierWarningDesc}</p>
          <ul className="space-y-1">
            {unverified.map((ref, idx) => (
              <li
                key={`${ref.kind}-${ref.text}-${idx}`}
                className="flex items-baseline gap-2"
              >
                <span className="font-mono text-[10px] uppercase tracking-wide text-amber-700 mt-px">
                  {s.verifierKindLabel[ref.kind] ?? ref.kind}
                </span>
                <span className="font-medium">{ref.text}</span>
              </li>
            ))}
          </ul>
          {report.verified_count > 0 && (
            <p className="mt-2 text-[11px] text-amber-700 border-t border-amber-200 pt-2">
              {s.verifierWarningFooter(report.verified_count)}
            </p>
          )}
        </div>
      )}
    </div>
  )
}

function SourceCard({ source }: { source: Source }) {
  const head =
    source.kb_title || source.case_name || source.citation || source.document_id || source.source_id || source.chunk_id
  const page = source.page_start
    ? source.page_end && source.page_end !== source.page_start
      ? `p.${source.page_start}–${source.page_end}`
      : `p.${source.page_start}`
    : null
  const scoreParts: string[] = []
  if (source.rrf_score != null)
    scoreParts.push(`rrf=${source.rrf_score.toFixed(4)}`)
  if (source.bm25_score != null)
    scoreParts.push(`bm25=${source.bm25_score.toFixed(4)}`)
  if (source.dense_score != null)
    scoreParts.push(`dense=${source.dense_score.toFixed(4)}`)

  return (
    <div className="bg-brand-50 border border-brand-200 rounded-lg p-3 text-xs">
      <div className="flex items-baseline justify-between gap-2 mb-1.5">
        <div className="flex-1 min-w-0">
          <span className="font-mono text-brand-500 mr-1.5">
            [{source.index}]
          </span>
          <span className="font-medium text-brand-800">{head}</span>
          {source.citation && source.case_name && (
            <span className="text-brand-400 ml-2 font-mono">
              {source.citation}
            </span>
          )}
          {page && (
            <span className="text-brand-400 ml-2">{page}</span>
          )}
        </div>
        {scoreParts.length > 0 && (
          <span className="text-brand-400 font-mono whitespace-nowrap text-[10px]">
            {scoreParts.join(' ')}
          </span>
        )}
      </div>
      <div className="text-brand-600 whitespace-pre-wrap line-clamp-4 leading-snug">
        {source.content.trim()}
      </div>
      <div className="text-brand-400 font-mono mt-1.5 text-[10px]">
        chunk={source.chunk_id}
        {source.document_type && (
          <span className="ml-2">type={source.document_type}</span>
        )}
      </div>
    </div>
  )
}
